import { Dtos, type ISODate, toISODate } from "@inrev/common";
import { Option } from "@inrev/core";
import { DateTime } from "luxon";

type FinancialPeriod = Dtos.Admin.FinancialPeriod.Get.Response;
type FinancialExtraction = Dtos.FinancialExtraction.Get.Response;

const FYE_EXPIRATION_MONTHS = 15;

export function isFyePeriodExpired(periodEndDate: ISODate): boolean {
	return (
		DateTime.fromISO(periodEndDate).plus({ months: FYE_EXPIRATION_MONTHS }).diffNow("days").days < 0
	);
}

export function daysUntilFyeExpiration(periodEndDate: ISODate): number {
	return DateTime.fromISO(periodEndDate).plus({ months: FYE_EXPIRATION_MONTHS }).diffNow("days")
		.days;
}

export function nextRequiredFyeDate(periodEndDate: ISODate): ISODate {
	const periodDt = DateTime.fromISO(periodEndDate);
	const yearsStale = Math.ceil(
		DateTime.now().minus({ months: FYE_EXPIRATION_MONTHS }).diff(periodDt, "years").years,
	);
	return toISODate(periodDt.plus({ years: Math.max(1, yearsStale) }));
}

export function getLatestFyePeriod(
	financialPeriods: Option<FinancialPeriod[]>,
): FinancialPeriod | undefined {
	return financialPeriods
		?.filter((p) => p.periodType === "fye")
		.sort((a, b) => b.periodEndDate.localeCompare(a.periodEndDate))[0];
}

export function getYtdPeriod(
	financialPeriods: Option<FinancialPeriod[]>,
): FinancialPeriod | undefined {
	return financialPeriods
		?.filter((p) => p.periodType === "ytd")
		.sort((a, b) => b.periodEndDate.localeCompare(a.periodEndDate))[0];
}

export function getYearPriorFyePeriod(
	financialPeriods: Option<FinancialPeriod[]>,
	latestFye: FinancialPeriod,
): FinancialPeriod | undefined {
	const expectedDate = toISODate(DateTime.fromISO(latestFye.periodEndDate).minus({ years: 1 }));
	return financialPeriods?.find((p) => p.periodType === "fye" && p.periodEndDate === expectedDate);
}

export function hasCompletedFinancialExtractionForFye(
	financialExtractions: Option<FinancialExtraction[]>,
	periodEndDate: ISODate,
): boolean {
	return (
		financialExtractions?.some(
			(e) =>
				e.status === "completed" && e.periodType === "fye" && e.periodEndDate === periodEndDate,
		) ?? false
	);
}

export function latestFyeMissing(
	financialPeriods: Option<FinancialPeriod[]>,
	financialExtractions: Option<FinancialExtraction[]>,
): boolean {
	if (!financialPeriods?.length) return true;

	const latestFye = getLatestFyePeriod(financialPeriods);
	if (!latestFye) return true;

	return !hasCompletedFinancialExtractionForFye(financialExtractions, latestFye.periodEndDate);
}

export function yearPriorFyeMissing(
	financialPeriods: Option<FinancialPeriod[]>,
	financialExtractions: Option<FinancialExtraction[]>,
): { missing: true; periodEndDate: ISODate } | { missing: false } {
	const latestFye = getLatestFyePeriod(financialPeriods);
	if (!latestFye) return { missing: false };

	const expectedDate = toISODate(DateTime.fromISO(latestFye.periodEndDate).minus({ years: 1 }));

	const yearPriorFye = getYearPriorFyePeriod(financialPeriods, latestFye);

	if (!yearPriorFye) {
		return { missing: true, periodEndDate: expectedDate };
	}

	if (!hasCompletedFinancialExtractionForFye(financialExtractions, yearPriorFye.periodEndDate)) {
		return { missing: true, periodEndDate: yearPriorFye.periodEndDate };
	}

	return { missing: false };
}
